<script lang="ts" setup>
import { onMounted, ref, computed, nextTick, watch } from 'vue'
import ThreadItem from './ThreadItem.vue'
import { useThreadsStore } from '@/store/threads-store'
import { storeToRefs } from 'pinia'
import TextView from '@/ui/common/text-view/TextView.vue'
import { useI18n } from 'vue-i18n'
import renameIcon from '@/assets/icons/threads/rename.svg'
import exportIcon from '@/assets/icons/threads/export.svg'
import deleteIcon from '@/assets/icons/threads/delete.svg'
import { MessagesApi } from '@/api/modules/message'
import { useRouter } from 'vue-router'
import { Routes } from '@/config/router/routes'
import ConfirmDeleteDialog from '@/ui/common/dialogs/ConfirmDeleteDialog.vue'
import ExportDialog from '@/ui/common/dialogs/ExportDialog.vue'

import { openFileInNewTab } from '@/utils/file-utils'
import type { TExportFormat } from '@/api/modules/message/types'

const { t, locale } = useI18n()

interface Props {
  whenClickItem: (id: string) => void
  selectedThreadId: string
}

const props = defineProps<Props>()

const router = useRouter()

const threadsStore = useThreadsStore()
const {
  handleGetThreads,
  getNextTodayThreads,
  getNextYesterdayDataThreads,
  getNextWeekDataThreads,
} = threadsStore

const {
  loading,
  todayItems,
  yesterdayItems,
  weekItems,
  hasNextTodayData,
  hasNextYesterdayData,
  hasNextWeekData,
} = storeToRefs(threadsStore)

const hoveredThreadId = ref<string | null>(null)
const openedContextMenuId = ref<string | null>(null)

const editingThreadId = ref<string | null>(null)
const editValue = ref<string>('')

const todayLoading = ref(false)
const yesterdayLoading = ref(false)
const weekLoading = ref(false)

const renamingThreadId = ref<string | null>(null)
const exportingThreadId = ref<string | null>(null)
const deletingThreadId = ref<string | null>(null)

const showExportDialog = ref(false)
const showDeleteConfirmDialog = ref(false)
const threadToExport = ref<string | null>(null)
const threadToDelete = ref<string | null>(null)

const handleContextMenuOpen = (threadId: string, isOpen: boolean) => {
  if (isOpen) {
    openedContextMenuId.value = threadId
  } else if (openedContextMenuId.value === threadId) {
    openedContextMenuId.value = null
  }
}

const closeAllContextMenus = () => {
  openedContextMenuId.value = null
}

const findThreadById = (threadId: string) => {
  const allThreads = [...todayItems.value, ...yesterdayItems.value, ...weekItems.value]
  return allThreads.find((t) => t.thread_id === threadId)
}

const handleRenameThread = async (threadId: string) => {
  const thread = findThreadById(threadId)
  if (!thread) return

  editingThreadId.value = threadId
  editValue.value = thread.last_message
  closeAllContextMenus()

  await nextTick()
}

const handleEditSave = async (threadId: string) => {
  const newName = editValue.value.trim()
  const thread = findThreadById(threadId)

  if (!newName || !thread || newName === thread.last_message) {
    editingThreadId.value = null
    return
  }

  try {
    renamingThreadId.value = threadId

    await MessagesApi.updateThread(threadId, newName)

    thread.last_message = newName
  } catch (error) {
    console.error('Error renaming thread:', error)
    throw error
  } finally {
    editingThreadId.value = null
    renamingThreadId.value = null
  }
}

const handleEditCancel = (threadId: string) => {
  editingThreadId.value = null
  const thread = findThreadById(threadId)
  if (thread) {
    editValue.value = thread.last_message
  }
}

const handleExportDialogOpen = (threadId: string) => {
  threadToExport.value = threadId
  showExportDialog.value = true
  closeAllContextMenus()
}

const handleExportDialogClose = () => {
  showExportDialog.value = false
  threadToExport.value = null
}

const handleExportThread = async (format: TExportFormat) => {
  if (!threadToExport.value) return

  try {
    exportingThreadId.value = threadToExport.value

    const response = await MessagesApi.exportThread({
      thread_id: threadToExport.value,
      export_format: format,
    })

    if (response.data.download_url) {
      openFileInNewTab(response.data.download_url, response.data.filename)
    } else {
      throw new Error('No download URL received')
    }
  } catch (error) {
    console.error('Error exporting thread:', error)
    throw error
  } finally {
    exportingThreadId.value = null
    handleExportDialogClose()
  }
}

const handleDeleteDialogOpen = (threadId: string) => {
  threadToDelete.value = threadId
  showDeleteConfirmDialog.value = true
  closeAllContextMenus()
}

const handleDeleteDialogClose = () => {
  showDeleteConfirmDialog.value = false
  threadToDelete.value = null
}

const handleDeleteThread = async () => {
  if (!threadToDelete.value) return

  try {
    deletingThreadId.value = threadToDelete.value

    await MessagesApi.deleteThread(threadToDelete.value)

    const removeFromArray = (arr: any[]) => {
      const index = arr.findIndex((item) => item.thread_id === threadToDelete.value)
      if (index !== -1) {
        arr.splice(index, 1)
      }
    }

    removeFromArray(todayItems.value)
    removeFromArray(yesterdayItems.value)
    removeFromArray(weekItems.value)

    if (props.selectedThreadId === threadToDelete.value) {
      await router.push({ name: Routes.Chat })
    }
  } catch (error) {
    console.error('Error deleting thread:', error)
    throw error
  } finally {
    deletingThreadId.value = null
    handleDeleteDialogClose()
  }
}

const isRenaming = (threadId: string) => renamingThreadId.value === threadId
const isExporting = (threadId: string) => exportingThreadId.value === threadId
const isDeleting = (threadId: string) => deletingThreadId.value === threadId

const hasMoreToday = computed(() => !!hasNextTodayData.value)
const hasMoreYesterday = computed(() => !!hasNextYesterdayData.value)
const hasMoreWeek = computed(() => !!hasNextWeekData.value)

const handleShowMoreToday = async () => {
  if (todayLoading.value) return
  try {
    todayLoading.value = true
    await getNextTodayThreads('today')
  } finally {
    todayLoading.value = false
  }
}

const handleShowMoreYesterday = async () => {
  if (yesterdayLoading.value) return
  try {
    yesterdayLoading.value = true
    await getNextYesterdayDataThreads('yesterday')
  } finally {
    yesterdayLoading.value = false
  }
}

const handleShowMoreWeek = async () => {
  if (weekLoading.value) return
  try {
    weekLoading.value = true
    await getNextWeekDataThreads('previous_7_days')
  } finally {
    weekLoading.value = false
  }
}

const menuItems = computed(() => (threadId: string) => {
  if (editingThreadId.value === threadId) {
    return [
      {
        key: 'rename',
        label: t('menu.rename'),
        icon: renameIcon,
        onClick: () => handleRenameThread(threadId),
        color: 'var(--gray_80)',
        isLoading: isRenaming(threadId),
        isDisabled: false,
      },
    ]
  }

  if (editingThreadId.value !== null) {
    return []
  }

  return [
    {
      key: 'rename',
      label: t('menu.rename'),
      icon: renameIcon,
      onClick: () => handleRenameThread(threadId),
      color: 'var(--gray_80)',
      isLoading: isRenaming(threadId),
      isDisabled: false,
    },
    {
      key: 'export',
      label: t('menu.export'),
      icon: exportIcon,
      onClick: () => handleExportDialogOpen(threadId),
      color: 'var(--gray_80)',
      isLoading: isExporting(threadId),
      isDisabled: false,
    },
    {
      key: 'delete',
      label: t('menu.delete'),
      icon: deleteIcon,
      onClick: () => handleDeleteDialogOpen(threadId),
      color: 'var(--error_50)',
      isLoading: isDeleting(threadId),
      isDisabled: false,
    },
  ]
})

const handleEditInput = (value: string) => {
  editValue.value = value
}

const getThreadTitle = (threadId: string) => {
  const thread = findThreadById(threadId)
  return thread?.last_message || ''
}

watch(
  () => locale.value,
  async () => await handleGetThreads(),
)

onMounted(handleGetThreads)
</script>

<template>
  <div class="threads-wrapper">
    <div v-if="loading" class="threads-wrapper__spinner">
      <ASpin />
    </div>
    <div v-else class="scrollable">
      <div v-if="todayItems.length > 0" class="threads-block">
        <TextView variant="roboto_13_regular" class="threads-block-label">{{
          t('ui.search-today')
        }}</TextView>
        <ThreadItem
          v-for="threadItem in todayItems"
          :key="threadItem.thread_id"
          :thread-item="threadItem"
          :is-selected="threadItem.thread_id === props.selectedThreadId"
          :menu-items="menuItems(threadItem.thread_id)"
          :is-hovered="hoveredThreadId === threadItem.thread_id"
          :is-context-menu-open="openedContextMenuId === threadItem.thread_id"
          :is-editing="editingThreadId === threadItem.thread_id"
          :edit-value="editValue"
          :when-click-thread-item="() => props.whenClickItem(threadItem.thread_id)"
          @mouseenter="hoveredThreadId = threadItem.thread_id"
          @mouseleave="hoveredThreadId = null"
          @context-menu-open="(isOpen) => handleContextMenuOpen(threadItem.thread_id, isOpen)"
          @edit-input="handleEditInput"
          @edit-save="() => handleEditSave(threadItem.thread_id)"
          @edit-cancel="() => handleEditCancel(threadItem.thread_id)"
        />
        <div class="threads-show-more">
          <a-button
            v-if="hasMoreToday"
            class="threads-show-more__button"
            @click="handleShowMoreToday"
          >
            <TextView variant="roboto_13_regular">{{ t('ui.show-more') }}</TextView>
          </a-button>
        </div>
      </div>
      <div v-if="yesterdayItems.length > 0" class="threads-block">
        <TextView variant="roboto_13_regular" class="threads-block-label">{{
          t('ui.search-yesterday')
        }}</TextView>
        <ThreadItem
          v-for="threadItem in yesterdayItems"
          :key="threadItem.thread_id"
          :thread-item="threadItem"
          :is-selected="threadItem.thread_id === props.selectedThreadId"
          :menu-items="menuItems(threadItem.thread_id)"
          :is-hovered="hoveredThreadId === threadItem.thread_id"
          :is-context-menu-open="openedContextMenuId === threadItem.thread_id"
          :is-editing="editingThreadId === threadItem.thread_id"
          :edit-value="editValue"
          :when-click-thread-item="() => props.whenClickItem(threadItem.thread_id)"
          @mouseenter="hoveredThreadId = threadItem.thread_id"
          @mouseleave="hoveredThreadId = null"
          @context-menu-open="(isOpen) => handleContextMenuOpen(threadItem.thread_id, isOpen)"
          @edit-input="handleEditInput"
          @edit-save="() => handleEditSave(threadItem.thread_id)"
          @edit-cancel="() => handleEditCancel(threadItem.thread_id)"
        />
        <div class="threads-show-more">
          <a-button
            v-if="hasMoreYesterday"
            class="threads-show-more__button"
            @click="handleShowMoreYesterday"
          >
            <TextView variant="roboto_13_regular">{{ t('ui.show-more') }}</TextView>
          </a-button>
        </div>
      </div>
      <div v-if="weekItems.length > 0" class="threads-block">
        <TextView variant="roboto_13_regular" class="threads-block-label">{{
          t('ui.search-week')
        }}</TextView>
        <ThreadItem
          v-for="threadItem in weekItems"
          :key="threadItem.thread_id"
          :thread-item="threadItem"
          :is-selected="threadItem.thread_id === props.selectedThreadId"
          :menu-items="menuItems(threadItem.thread_id)"
          :is-hovered="hoveredThreadId === threadItem.thread_id"
          :is-context-menu-open="openedContextMenuId === threadItem.thread_id"
          :is-editing="editingThreadId === threadItem.thread_id"
          :edit-value="editValue"
          :when-click-thread-item="() => props.whenClickItem(threadItem.thread_id)"
          @mouseenter="hoveredThreadId = threadItem.thread_id"
          @mouseleave="hoveredThreadId = null"
          @context-menu-open="(isOpen) => handleContextMenuOpen(threadItem.thread_id, isOpen)"
          @edit-input="handleEditInput"
          @edit-save="() => handleEditSave(threadItem.thread_id)"
          @edit-cancel="() => handleEditCancel(threadItem.thread_id)"
        />
        <div class="threads-show-more">
          <a-button
            v-if="hasMoreWeek"
            class="threads-show-more__button"
            @click="handleShowMoreWeek"
          >
            <TextView variant="roboto_13_regular">{{ t('ui.show-more') }}</TextView>
          </a-button>
        </div>
      </div>
    </div>

    <ExportDialog
      :is-open="showExportDialog"
      :is-loading="threadToExport ? isExporting(threadToExport) : false"
      @close="handleExportDialogClose"
      @export="handleExportThread"
    />

    <ConfirmDeleteDialog
      :is-open="showDeleteConfirmDialog"
      :thread-title="threadToDelete ? getThreadTitle(threadToDelete) : ''"
      :is-loading="threadToDelete ? isDeleting(threadToDelete) : false"
      @close="handleDeleteDialogClose"
      @confirm="handleDeleteThread"
    />
  </div>
</template>

<style lang="scss">
.threads-wrapper {
  position: relative;
  overflow-y: scroll;
  height: 100%;
  margin-bottom: 16px;
  width: 304px;

  .threads-wrapper__spinner {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
  }

  .scrollable {
    position: absolute;
    width: 100%;
  }

  .threads-block {
    position: relative;
    width: 100%;
    padding: 12px 0px;
    border-bottom: 1px solid var(--gray_20);

    &-label {
      padding: 6px 24px;
      width: 100%;
      color: var(--gray_40);
    }

    &:last-child {
      border-bottom: none;
    }
  }

  .threads-show-more {
    padding: 0px 12px;
    margin-top: 8px;
    &__button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      margin: 0;
      padding: 6px 24px;

      border: none;
      cursor: pointer;
      color: var(--primary_50);

      border: initial;
      box-shadow: initial;

      background-color: var(--gray_20);
    }
  }
}
</style>
